import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Users, Eye, Trash2, FileDown, ArrowUpDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import AuthImage from "../components/AuthImage";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/date";

const statusMap = {
  active: { label: "Activo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  inactive: { label: "Inactivo", cls: "bg-zinc-100 text-zinc-500 border-zinc-200" },
  suspended: { label: "Suspendido", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  terminated: { label: "Terminado", cls: "bg-red-50 text-red-700 border-red-200" },
};

const genderMap = { masculino: "Masculino", femenino: "Femenino", M: "Masculino", F: "Femenino" };

const EXPORT_COLUMNS = [
  { key: "codigo", label: "Código" },
  { key: "identificacion", label: "Identificación" },
  { key: "genero", label: "Género" },
  { key: "ocupacion", label: "Ocupación" },
  { key: "empresa", label: "Empresa" },
  { key: "sucursal", label: "Sucursal" },
  { key: "departamento", label: "Departamento" },
  { key: "tipo_contrato", label: "Tipo de contrato" },
  { key: "jornada", label: "Jornada" },
  { key: "telefono", label: "Teléfono" },
  { key: "email", label: "Correo" },
  { key: "nacimiento", label: "Fecha de nacimiento" },
  { key: "ingreso", label: "Fecha de ingreso" },
  { key: "salario", label: "Salario base" },
  { key: "estado", label: "Estado" },
];
const DEFAULT_EXPORT_COLS = ["codigo", "identificacion", "genero", "ocupacion", "empresa", "sucursal", "ingreso", "estado"];

function fmtDate(iso) {
  return formatDate(iso);
}

function EmpAvatar({ emp }) {
  const initials = (emp.full_name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const fallback = (
    <div className="w-9 h-9 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">
      {initials}
    </div>
  );
  if (!emp.photo_path) return fallback;
  return (
    <AuthImage
      path={emp.photo_path}
      alt={emp.full_name}
      className="w-9 h-9 rounded-full object-cover border border-zinc-200 shrink-0"
      fallback={fallback}
    />
  );
}

export default function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterBranch, setFilterBranch] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCompany, setFilterCompany] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportCols, setExportCols] = useState(DEFAULT_EXPORT_COLS);
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const buildFilterParams = useCallback(() => {
    const params = {};
    if (filterBranch !== "all") params.branch_id = filterBranch;
    if (filterStatus !== "all") params.status = filterStatus;
    if (filterCompany !== "all") params.company_id = filterCompany;
    if (search) params.search = search;
    params.sort = sortBy;
    return params;
  }, [filterBranch, filterStatus, filterCompany, search, sortBy]);

  const fetchData = useCallback(async () => {
    try {
      const params = buildFilterParams();
      const [empRes, brRes, deptRes, occRes, coRes] = await Promise.all([
        api.get("/employees", { params }),
        api.get("/branches"),
        api.get("/departments"),
        api.get("/occupations"),
        api.get("/companies").catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data.items || []);
      setBranches(brRes.data);
      setDepartments(deptRes.data);
      setOccupations(occRes.data);
      setCompanies(coRes.data || []);
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const runExport = () => {
    const cols = EXPORT_COLUMNS.filter((c) => exportCols.includes(c.key)).map((c) => c.key);
    if (cols.length === 0) return;
    const params = buildFilterParams();
    if (params.sort === "recent") params.sort = "name_asc";
    params.columns = cols.join(",");
    const qs = new URLSearchParams(params).toString();
    window.open(`${apiBase}/api/employees/export/pdf?${qs}`, "_blank");
    setExportOpen(false);
  };

  const toggleCol = (key) =>
    setExportCols((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const getBranchName = (id) => branches.find((b) => b.id === id)?.name || "\u2014";
  const getDeptName = (id) => departments.find((d) => d.id === id)?.name || "\u2014";
  const getOccName = (id) => occupations.find((o) => o.id === id)?.name || "\u2014";
  const getCompanyName = (id) => companies.find((c) => c.id === id)?.legal_name || "\u2014";

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este empleado y todo su expediente?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Empleado eliminado");
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar empleado");
    }
  };

  return (
    <div data-testid="employees-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
            Empleados
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Gestion del personal de la empresa
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setExportOpen(true)}
            variant="outline"
            className="border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            data-testid="export-employees-pdf"
          >
            <FileDown className="w-4 h-4 mr-2" /> Exportar PDF
          </Button>
          <Button
            onClick={() => navigate("/empleados/nuevo")}
            className="bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="add-employee-button"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo trabajador
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            className="pl-9"
            placeholder="Buscar por nombre, codigo o cedula..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="employee-search-input"
          />
        </div>
        <Select value={filterBranch} onValueChange={setFilterBranch}>
          <SelectTrigger className="w-[180px]" data-testid="employee-filter-branch">
            <SelectValue placeholder="Sucursal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las sucursales</SelectItem>
            {branches.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {companies.length > 0 && (
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="w-[200px]" data-testid="employee-filter-company">
              <SelectValue placeholder="Empresa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las empresas</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.legal_name}{c.is_primary ? " ★" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]" data-testid="employee-filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activo</SelectItem>
            <SelectItem value="inactive">Inactivo</SelectItem>
            <SelectItem value="suspended">Suspendido</SelectItem>
            <SelectItem value="terminated">Terminado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[190px]" data-testid="employee-sort">
            <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 text-zinc-400" />
            <SelectValue placeholder="Ordenar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Más recientes</SelectItem>
            <SelectItem value="name_asc">Nombre (A-Z)</SelectItem>
            <SelectItem value="name_desc">Nombre (Z-A)</SelectItem>
            <SelectItem value="hire_desc">Ingreso más reciente</SelectItem>
            <SelectItem value="hire_asc">Mayor antigüedad</SelectItem>
            <SelectItem value="code_asc">Código (A-Z)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Trabajador</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden sm:table-cell">Identificación</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden lg:table-cell">Género</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">Ocupación</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden lg:table-cell">Empresa</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden lg:table-cell">Sucursal</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden xl:table-cell">Ingreso</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">Estado</TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((emp) => (
                <TableRow
                  key={emp.id}
                  className="border-zinc-100 hover:bg-zinc-50/60 transition-colors cursor-pointer"
                  onClick={() => navigate(`/empleados/${emp.id}`)}
                  data-testid={`employee-row-${emp.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <EmpAvatar emp={emp} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-zinc-900 font-medium truncate">{emp.full_name}</span>
                          {emp.worker_type === "occasional" && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                              Ocasional
                            </Badge>
                          )}
                        </div>
                        <span className="text-[11px] text-zinc-400 font-mono">{emp.employee_code}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 font-mono hidden sm:table-cell">
                    {emp.national_id || "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden lg:table-cell">
                    {genderMap[emp.gender] || "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden md:table-cell">
                    {getOccName(emp.occupation_id)}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden lg:table-cell">
                    {getCompanyName(emp.company_id)}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden lg:table-cell">
                    {getBranchName(emp.primary_branch_id)}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 hidden xl:table-cell">
                    {fmtDate(emp.hire_date)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`font-medium ${statusMap[emp.status]?.cls || ""}`}
                    >
                      {statusMap[emp.status]?.label || emp.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Ver expediente"
                        onClick={(e) => { e.stopPropagation(); navigate(`/empleados/${emp.id}`); }}
                        data-testid={`view-employee-${emp.id}`}
                      >
                        <Eye className="w-4 h-4 text-zinc-500" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        title="Eliminar"
                        onClick={(e) => handleDelete(e, emp.id)}
                        data-testid={`delete-employee-${emp.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {employees.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-16 text-zinc-400">
                    <Users className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay empleados registrados</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle>Exportar lista de trabajadores (PDF)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">
              Se exportarán <b>{employees.length}</b> trabajador(es) según los filtros y el orden
              actuales. Seleccione las columnas que desea incluir:
            </p>
            <div className="flex items-center gap-3 text-[11px]">
              <button
                type="button"
                onClick={() => setExportCols(EXPORT_COLUMNS.map((c) => c.key))}
                className="text-zinc-700 underline hover:text-zinc-900"
                data-testid="export-select-all"
              >
                Seleccionar todo
              </button>
              <button
                type="button"
                onClick={() => setExportCols(DEFAULT_EXPORT_COLS)}
                className="text-zinc-500 underline hover:text-zinc-800"
                data-testid="export-select-default"
              >
                Restablecer
              </button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-[320px] overflow-y-auto pr-1">
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-not-allowed">
                <Checkbox checked disabled /> Trabajador (siempre)
              </label>
              {EXPORT_COLUMNS.map((c) => (
                <label
                  key={c.key}
                  className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer"
                >
                  <Checkbox
                    checked={exportCols.includes(c.key)}
                    onCheckedChange={() => toggleCol(c.key)}
                    data-testid={`export-col-${c.key}`}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportOpen(false)} data-testid="export-cancel">
              Cancelar
            </Button>
            <Button
              onClick={runExport}
              disabled={exportCols.length === 0}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="export-generate"
            >
              <FileDown className="w-4 h-4 mr-2" /> Generar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
